/**
 * DocumentQueueNav.tsx
 *
 * 実施業務からの一括作成で生まれた複数の下書きを「1件ずつ体裁を整えて発行する」ための巡回ナビ。
 * 以前は先頭の1件へ遷移するだけで、司法書士請求書を仕上げると見積書へ飛ぶ一本道になっており、
 * 行政書士請求書・調査士請求書・土地/建物計算書の下書きが放置されていた。
 *
 * ここでは残件を常時見える形にし、どの書類へも直接ジャンプできるようにする。
 */
import { getDocTypeLabel, type DocumentSummary } from "../../api.js";
import { Button } from "../ui/Button.js";
import { useTenantDocumentTypes } from "../../hooks/useDocTypeProfession.js";

export type QueueEntry = {
  id:       string;
  doc:      DocumentSummary | undefined;
  /** 発行済み（＝仕上げ完了）か */
  done:     boolean;
  isCurrent: boolean;
};

type Props = {
  entries:      QueueEntry[];
  /** 次に仕上げるべき書類（未発行）。全て発行済みなら null */
  nextPendingId: string | null;
  /** 編集モード中か（別の書類へ移ると編集内容が保存されないことを知らせる） */
  editing:      boolean;
  onOpen:       (docId: string) => void;
  onFinish:     () => void;
  onDismiss:    () => void;
};

export function DocumentQueueNav({ entries, nextPendingId, editing, onOpen, onFinish, onDismiss }: Props) {
  const total     = entries.length;
  const doneCount = entries.filter((e) => e.done).length;
  const allDone   = doneCount === total;
  const position  = entries.findIndex((e) => e.isCurrent) + 1;
  // カスタム書類種別（tenant_document_types）のラベル解決（reviewer 指摘対応）
  const { data: docTypesData } = useTenantDocumentTypes();
  function docTypeLabel(docType: string): string {
    return docTypesData?.data.find((d) => d.doc_type === docType)?.label ?? getDocTypeLabel(docType);
  }

  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid #99f6e4",
        borderRadius: 10,
        padding: "12px 14px",
        marginBottom: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
            まとめて作成した書類の仕上げ
            <span style={{ fontSize: 12, fontWeight: 700, color: "#0f766e", marginLeft: 8 }}>
              {position > 0 ? `${position} / ${total} 件目` : `${total} 件`}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {allDone
              ? "すべて発行済みです。PDFをまとめてダウンロードできます。"
              : `発行済み ${doneCount} / ${total} 件。残りの書類も体裁を整えて発行してください。`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Button
            variant="ghost"
            style={{ minHeight: "auto", padding: "6px 12px", fontSize: 12, fontWeight: 600 }}
            onClick={onDismiss}
          >
            この案内を閉じる
          </Button>
          {allDone ? (
            <Button
              variant="primary"
              style={{ minHeight: "auto", padding: "6px 14px", fontSize: 12, fontWeight: 700 }}
              onClick={onFinish}
            >
              発行済みPDF一覧へ →
            </Button>
          ) : nextPendingId ? (
            <Button
              variant="primary"
              style={{ minHeight: "auto", padding: "6px 14px", fontSize: 12, fontWeight: 700 }}
              onClick={() => onOpen(nextPendingId)}
            >
              次の書類へ →
            </Button>
          ) : null}
        </div>
      </div>

      {editing && (
        <div style={{ fontSize: 11, color: "#c2410c", marginTop: 8 }}>
          ⚠ この書類は編集中です。別の書類へ移ると編集内容は保存されません。
        </div>
      )}

      {/* 進行状況。クリックで任意の書類へジャンプできる */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
        {entries.map((entry, index) => {
          const label = entry.doc ? docTypeLabel(entry.doc.doc_type) : "（削除済み）";
          const missing = !entry.doc;
          return (
            <button
              key={entry.id}
              type="button"
              disabled={entry.isCurrent || missing}
              onClick={() => onOpen(entry.id)}
              title={missing ? "この書類は見つかりません" : label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 999,
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: entry.isCurrent ? 800 : 600,
                color: missing ? "#94a3b8" : entry.isCurrent ? "#0f172a" : entry.done ? "#0f766e" : "#475569",
                background: entry.isCurrent ? "#ccfbf1" : entry.done ? "#f0fdfa" : "#f8fafc",
                border: `1px solid ${entry.isCurrent ? "#0f766e" : entry.done ? "#99f6e4" : "#e2e8f0"}`,
                cursor: entry.isCurrent || missing ? "default" : "pointer",
                opacity: missing ? 0.6 : 1,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 800, opacity: 0.7 }}>{index + 1}</span>
              <span>{label}</span>
              <span style={{ fontSize: 11 }}>{entry.done ? "✓" : entry.isCurrent ? "●" : "○"}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
